/**
 * 会員一覧が扱う「環境」の共有定義。
 * API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）が共有する。
 *
 * 会員一覧は本番会員とSTG会員の両方を扱う。STG 側はテスト用アカウントを探すための
 * タブで、本番とは別の権限・別のデータセットで動く。値を両側で独立に持つと、
 * 「画面は STG タブを出すのに API は本番を返す」ような壊れ方をする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Shares the member list environment identifiers, BigQuery dataset names, and permission keys between the API and the Web so both sides cannot drift.
 * @graph-connects none
 */
/**
 * 会員一覧の環境。
 *
 * `production` は本番会員、`staging` は STG（テスト環境）の会員を指す。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Lists the environments the member list can display.
 * @graph-connects none
 */
export const MEMBER_ENVIRONMENTS = ['production', 'staging'];
/**
 * 本番会員データを置く BigQuery データセット。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Names the BigQuery dataset holding production member data.
 * @graph-connects none
 */
export const AC_MENS_HUB_DATASET = 'ac_mens_hub';
/**
 * STG 会員データを置く BigQuery データセット。
 *
 * STG ミラー（`*_STG_*`）と、本番と同名の mart VIEW をここに置く。
 * インフラ側（`infra/generator/ac-mens-hub-daily`）が作るデータセットと同じ値でなければ、
 * API が存在しないデータセットを読んで全件 0 件になる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Names the BigQuery dataset holding the staging member mirror and mart views.
 * @graph-connects none
 */
export const AC_MENS_HUB_STG_DATASET = 'ac_mens_hub_stg';
/**
 * 本番の会員一覧を見る権限。
 *
 * 本番会員の氏名・電話を含む情報を扱うため、付与先は限定する。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Names the permission required to view production members.
 * @graph-connects none
 */
export const MEMBER_PERMISSION = 'member';
/**
 * STG の会員一覧を見る権限。
 *
 * `MEMBER_PERMISSION` とは**独立した権限**で、片方だけの保有が成立する。
 * これにより「本番は見られないが、STG のテストアカウントは探せる」人を作れる。
 *
 * STG は本番スナップショットから作り直されるため氏名等が本番相当で残る。
 * この権限だけを持つ人に氏名・電話を見せてはならない（画面側でマスクする）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Names the permission required to view staging members, granted independently from the production one.
 * @graph-connects none
 */
export const MEMBER_STAGING_PERMISSION = 'member-staging';
/**
 * 環境ごとの BigQuery データセットを引く。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Resolves which BigQuery dataset holds the members for the given environment.
 * @graph-connects none
 */
export function memberDatasetFor(environment) {
    return environment === 'staging' ? AC_MENS_HUB_STG_DATASET : AC_MENS_HUB_DATASET;
}
/**
 * 環境ごとに必要な権限キーを引く。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Resolves which permission key guards the member list for the given environment.
 * @graph-connects none
 */
export function memberPermissionFor(environment) {
    return environment === 'staging' ? MEMBER_STAGING_PERMISSION : MEMBER_PERMISSION;
}
//# sourceMappingURL=member-environments.js.map